// diagnostics.js — a small ring buffer of what went wrong in this tab, so a
// Feedback report carries the evidence a screenshot cannot: recent console
// errors, uncaught exceptions, and API calls that failed.
//
// Installed once at startup (index.js). It records; it never changes behaviour:
// console.error still prints, fetch still returns the same response, and every
// hook is wrapped in try/catch so a fault here cannot break the app.
//
// Nothing about a request's body or headers is kept — only method, URL and
// status — and the server redacts signature-like query parameters anyway.

const MAX = 20;
const state = { errors: [], failedRequests: [], installed: false };

function push(list, item) {
  list.push(item);
  if (list.length > MAX) list.splice(0, list.length - MAX);
}

export function describeError(args) {
  return args.map((a) => {
    if (a instanceof Error) return `${a.name}: ${a.message}`;
    if (typeof a === 'string') return a;
    try { return JSON.stringify(a); } catch { return String(a); }
  }).join(' ').slice(0, 500);
}

export function recordError(message) {
  push(state.errors, { at: new Date().toISOString(), message: String(message || '').slice(0, 500) });
}

export function recordFailedRequest(method, url, status) {
  push(state.failedRequests, { at: new Date().toISOString(), method: String(method || 'GET').toUpperCase(), url: String(url || ''), status });
}

export function installDiagnostics(win = typeof window !== 'undefined' ? window : null) {
  if (!win || state.installed) return;
  state.installed = true;
  try {
    const orig = win.console && win.console.error;
    if (orig) {
      win.console.error = function patchedConsoleError(...args) {
        try { recordError(describeError(args)); } catch { /* never break logging */ }
        return orig.apply(this, args);
      };
    }
    win.addEventListener('error', (e) => { try { recordError(e && (e.message || (e.error && e.error.message))); } catch { /* ignore */ } });
    win.addEventListener('unhandledrejection', (e) => { try { recordError(`Unhandled rejection: ${describeError([e && e.reason])}`); } catch { /* ignore */ } });
    const origFetch = win.fetch;
    if (origFetch) {
      win.fetch = async function patchedFetch(input, init) {
        const method = (init && init.method) || (input && input.method) || 'GET';
        const url = typeof input === 'string' ? input : (input && input.url) || '';
        try {
          const res = await origFetch.call(this, input, init);
          try { if (!res.ok && /\/api\//.test(url)) recordFailedRequest(method, url, res.status); } catch { /* ignore */ }
          return res;
        } catch (err) {
          try { if (/\/api\//.test(url)) recordFailedRequest(method, url, null); } catch { /* ignore */ }
          throw err;
        }
      };
    }
  } catch { /* diagnostics are best-effort */ }
}

export function snapshotDiagnostics({ page, win = typeof window !== 'undefined' ? window : null } = {}) {
  return {
    url: win ? win.location.href : '',
    page: page || '',
    userAgent: win && win.navigator ? win.navigator.userAgent : '',
    viewport: win ? `${win.innerWidth}x${win.innerHeight}` : '',
    capturedAt: new Date().toISOString(),
    errors: state.errors.slice(),
    failedRequests: state.failedRequests.slice(),
  };
}

// For tests.
export function _resetDiagnostics() {
  state.errors = []; state.failedRequests = []; state.installed = false;
}
